import { Connection, FeeCalculator, PublicKey } from '@solana/web3.js';
import { SetupInstructions, TransactionFeeInfo } from './types';
import { deserializeAccount } from '@mercurial-finance/optimist';

const SERUM_OPEN_ACCOUNT_LAMPORTS = 23_352_760;
const OPEN_TOKEN_ACCOUNT_LAMPORTS = 2_039_280;
const WALLET_RENT_EXEMPT_MINIMUM = 890_880;
const LAMPORTS_PER_SIGNATURE = 5000;

function sum(values: number[]) {
  return values.reduce((value, acc) => {
    acc += value;
    return acc;
  }, 0);
}

export const calculateTransactionDepositAndFee = ({
  intermediates,
  destination,
  openOrders,
  hasWrapUnwrapSOL,
  feeCalculator,
}: SetupInstructions & {
  hasWrapUnwrapSOL: boolean;
  feeCalculator: FeeCalculator;
}): TransactionFeeInfo => {
  const openOrdersDeposits = openOrders
    .filter((ooi) => ooi && ooi.setupInstructions.length > 0)
    .map(() => SERUM_OPEN_ACCOUNT_LAMPORTS);
  const ataDeposits = [...intermediates, destination]
    .filter((item) => item?.setupInstructions.length && item.cleanupInstructions.length === 0)
    .map(() => OPEN_TOKEN_ACCOUNT_LAMPORTS);

  const signatureFee = feeCalculator.lamportsPerSignature;

  const totalFeeAndDeposits = sum([signatureFee, ...openOrdersDeposits, ...ataDeposits]);

  // We need to account for temporary wrapped SOL token accounts as intermediary or output
  const minimumSOLForTransaction = sum([
    signatureFee,
    ...openOrdersDeposits,
    ...ataDeposits,
    WALLET_RENT_EXEMPT_MINIMUM,
    hasWrapUnwrapSOL ? OPEN_TOKEN_ACCOUNT_LAMPORTS : 0,
  ]);

  return {
    signatureFee,
    openOrdersDeposits,
    ataDeposits,
    totalFeeAndDeposits,
    minimumSOLForTransaction,
  };
};

interface CalculateTransactionFeeAndDepositParams {
  numOfAtasToBeCreated: number; // missing ata for each tokens
  numOfOpenOrdersToBeCreated: number; // missing open orders for each tokens
  isInputMintSOL: boolean;
}

export const calculateTransactionDepositAndFeeV2 = ({
  numOfAtasToBeCreated,
  numOfOpenOrdersToBeCreated,
  isInputMintSOL,
}: CalculateTransactionFeeAndDepositParams): TransactionFeeInfo => {
  const openOrdersDeposits = new Array(numOfOpenOrdersToBeCreated).fill(0).map(() => SERUM_OPEN_ACCOUNT_LAMPORTS);
  const ataDeposits = new Array(numOfAtasToBeCreated).fill(0).map(() => OPEN_TOKEN_ACCOUNT_LAMPORTS);

  const signatureFee = LAMPORTS_PER_SIGNATURE;

  const totalFeeAndDeposits = sum([signatureFee, ...openOrdersDeposits, ...ataDeposits]);

  // We need to account for temporary wrapped SOL token accounts as intermediary or output
  const minimumSOLForTransaction = sum([
    signatureFee,
    ...openOrdersDeposits,
    ...ataDeposits,
    isInputMintSOL ? WALLET_RENT_EXEMPT_MINIMUM : 0,
  ]);

  return {
    signatureFee,
    openOrdersDeposits,
    ataDeposits,
    totalFeeAndDeposits,
    minimumSOLForTransaction,
  };
};

interface GetReferralTokenAccountParams {
  connection: Connection;
  referralAccount: PublicKey;
  mint: PublicKey;
}

const REFERRAL_PROGRAM_ID = new PublicKey('REFER4ZgmyYx9c6He5XfaTMiGfdLwRnkV4RPp9t9iF3');
const REFERRAL_ATA_SEED = Buffer.from('referral_ata');

/** Provides the referral token account address if the account exists and is not frozen */
export const getReferralTokenAccount = async ({ connection, referralAccount, mint }: GetReferralTokenAccountParams) => {
  const referralTokenAccountAddress = PublicKey.findProgramAddressSync(
    [REFERRAL_ATA_SEED, referralAccount.toBuffer(), mint.toBuffer()],
    REFERRAL_PROGRAM_ID,
  )[0];
  const accountInfo = await connection.getAccountInfo(referralTokenAccountAddress);
  if (!accountInfo) return null;

  const tokenAccount = deserializeAccount(accountInfo.data);
  if (!tokenAccount) return null;
  if (tokenAccount.isFrozen) return null;

  return referralTokenAccountAddress;
};
